import { components } from "@/lib/ApiSchema";
import { getUrl } from "@/lib/useToPage";
import { useParams } from "react-router";
import QuantityInParent from "./QuantityInParent";
import StyledLink from "@/lib/StyledLink";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import useUpdateShopSettings from "@/lib/queries/useUpdateShopSettings";
import { useQueryClient } from "@tanstack/react-query";
import Switch from "@/lib/Switch";
import { Tooltip } from "react-tooltip";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";

export default function Header({
  part,
}: {
  part: components["schemas"]["RequestedPartWithOperationsSchema"];
}) {
  const { teamSlug, rfqId: rfqIdParam } = useParams();
  const parentLink = getUrl("part", {
    teamSlug: teamSlug!,
    rfqId: rfqIdParam!,
    partId: part.parent?.id ?? "",
  });

  const { data: shop } = useGetShopSettings(teamSlug);
  const updateShop = useUpdateShopSettings();
  const queryClient = useQueryClient();
  const lengthDisplayUnit = shop?.length_display_unit ?? "in";
  const canEdit =
    !!useGetCurrentQuoteRevision()?.can_edit && part.will_bid === null;

  return (
    <div className="flex justify-between items-center">
      <div>
        <div className="text-slate-500 text-xs">Part</div>
        <div className="flex items-baseline gap-2">
          <div className="font-bold">
            {part.part_number} <span className="text-slate-500">Rev</span>{" "}
            {part.revision_number}
          </div>
          {part.parent && (
            <>
              <QuantityInParent part={part} canEdit={canEdit} />
              <div className="text-sm">per</div>
              <StyledLink to={parentLink}>{part.parent.part_number}</StyledLink>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          value={lengthDisplayUnit === "mm"}
          label={lengthDisplayUnit}
          onChange={(isMetric) => {
            updateShop(
              {
                params: { path: { team_slug: teamSlug! } },
                body: {
                  length_display_unit: isMetric ? "mm" : "in",
                },
              },
              {
                onSuccess: () => {
                  // update general part info units
                  queryClient.invalidateQueries({
                    queryKey: [
                      "get",
                      "/api/teams/{team_slug}/requested_parts/{id}/extracted_info",
                      {
                        params: {
                          path: {
                            team_slug: teamSlug,
                            id: part.id,
                          },
                        },
                      },
                    ],
                  });
                },
              },
            );
          }}
          data-tooltip-id="length-display-unit-toggle"
          data-tooltip-content="Toggle between inches and millimeters"
          data-tooltip-delay-show={500}
        />
        <Tooltip id="length-display-unit-toggle" />
      </div>
    </div>
  );
}
